import React, { FC, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  School,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
} from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { StudentPerformanceViewProps } from '../performance.interfaces';
import { usePerformance } from '../performance.context';
import {
  getRatingColor,
  getStatusColor,
  getRatingLabel,
  getStatusLabel,
  formatScore,
  formatDate,
} from '../performance.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';
import PaginatedGrid from 'src/views/components/paginatedGrid/PaginatedGrid';

const PerformanceStudentsView: FC<StudentPerformanceViewProps> = ({
  reviews,
  summary,
  onReviewSelect,
}) => {
  const { fetchStudentReviews, state } = usePerformance();
  const { translateText } = useTranslation();

  useEffect(() => {
    if (reviews.length === 0) {
      fetchStudentReviews();
    }
  }, [reviews.length, fetchStudentReviews]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'student',
      headerName: translateText(
        'analytics.performance.students.columns.student'
      ),
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
            {params.row.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translateText('analytics.performance.students.id')}
              : {params.row.studentId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'gradeSection',
      headerName: translateText(
        'analytics.performance.students.columns.gradeSection'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {translateText('analytics.performance.students.grade')}{' '}
          {params.row.grade} - {params.row.section}
        </Typography>
      ),
    },
    {
      field: 'gpa',
      headerName: translateText(
        'analytics.performance.students.columns.gpa'
      ),
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {params.row.academicPerformance.gpa.toFixed(1)}
        </Typography>
      ),
    },
    {
      field: 'attendance',
      headerName: translateText(
        'analytics.performance.students.columns.attendance'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 120,
          }}
        >
          <LinearProgress
            variant="determinate"
            value={params.row.academicPerformance.attendance}
            sx={{ flexGrow: 1, mr: 1 }}
          />
          <Typography variant="body2">
            {params.row.academicPerformance.attendance}%
          </Typography>
        </Box>
      ),
    },
    {
      field: 'overallScore',
      headerName: translateText(
        'analytics.performance.students.columns.overallScore'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {formatScore(params.row.overallScore)}
        </Typography>
      ),
    },
    {
      field: 'rating',
      headerName: translateText(
        'analytics.performance.students.columns.rating'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getRatingLabel(
            params.row.overallRating,
            translateText
          )}
          size="small"
          sx={{
            backgroundColor: getRatingColor(params.row.overallRating),
            color: 'white',
            fontWeight: 'medium',
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: translateText(
        'analytics.performance.students.columns.status'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getStatusLabel(params.row.status, translateText)}
          size="small"
          variant="outlined"
          sx={{
            borderColor: getStatusColor(params.row.status),
            color: getStatusColor(params.row.status),
          }}
        />
      ),
    },
    {
      field: 'reviewDate',
      headerName: translateText(
        'analytics.performance.students.columns.reviewDate'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.row.reviewDate)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: translateText(
        'analytics.performance.students.columns.actions'
      ),
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip
          title={translateText(
            'analytics.performance.students.viewDetails'
          )}
        >
          <IconButton
            size="small"
            onClick={() => onReviewSelect(params.row)}
            color="primary"
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              minWidth: { xs: '44px', sm: 'auto' },
            }}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [translateText, onReviewSelect]);

  if (state.isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {translateText('analytics.performance.students.loading')}
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-testid="performance-students-view">
      {/* Summary Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <School color="primary" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.students.totalReviews')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.totalReviews}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.students.activeStudentReviews'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmojiEvents color="success" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.students.avgRating')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.averageRating.toFixed(1)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText('analytics.performance.students.outOf10')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp color="success" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.students.completed')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.completedReviews}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.students.reviewsCompleted'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingDown color="warning" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="warning.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.students.pending')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.pendingReviews}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText('analytics.performance.students.reviewsPending')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Distribution Chart */}
      <Card elevation={2} sx={{ mb: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText(
              'analytics.performance.students.performanceDistribution'
            )}
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {Object.entries(summary.ratingDistribution).map(
              ([rating, count]) => (
                <Grid item xs={6} sm={4} md={2.4} key={rating}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: getRatingColor(rating as any),
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                      }}
                    >
                      {count}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textTransform: 'capitalize',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {rating.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                  </Box>
                </Grid>
              )
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Student Reviews Table */}
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText(
              'analytics.performance.students.studentPerformanceReviews'
            )}
          </Typography>
          {reviews.length === 0 && !state.isLoading ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
              <School sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {translateText('analytics.performance.students.noReviewsFound')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.students.reviewsWillAppear'
                )}
              </Typography>
            </Box>
          ) : (
            <PaginatedGrid
              dataTestId="performance-students-grid"
              columns={columns}
              rows={reviews}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerformanceStudentsView;
